import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../commonDesign/Button'
import SydCheckbox from '../../../../commonDesign/SydCheckbox'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydInput from '../../../../commonDesign/SydInput'
import SydLabel from '../../../../commonDesign/SydLabel'
import Icon from '../../../../atoms/Icon'
import Markup from '../../../../atoms/Markup'
import { getWorkflowStatus, labelStyle, valueStyle as baseValueStyle } from '../shared/redtailUIHelpers'
import useWorkflowDetail from './useWorkflowDetail'

function getStepStatus (step, currentStepId) {
  if (step.completion_date) return 'complete'
  if (step.id === currentStepId) return 'current'
  return 'pending'
}

const STATUS_CONFIG = {
  complete: { icon: 'checkCircle', color: 'var(--color-growth-500)', label: 'Complete' },
  overdue: { icon: 'clock', color: 'var(--color-decline-500)', label: 'Overdue' },
  outstanding: { icon: 'radioUnchecked', color: 'var(--text-color-subtle)', label: 'Outstanding' }
}

const STEP_STATUS_CONFIG = {
  complete: { icon: 'checkCircle', color: 'var(--color-growth-500)' },
  current: { icon: 'circle', color: 'var(--color-primary-400)' },
  pending: { icon: 'radioUnchecked', color: 'var(--color-neutral-400)' }
}

const valueStyle = { ...baseValueStyle, marginBottom: '10px' }

/* ── Left panel: workflow summary + step list ──────────────────────── */

function WorkflowSummary ({ workflow }) {
  const status = getWorkflowStatus(workflow)
  const cfg = STATUS_CONFIG[status]
  const contactName = workflow.linked_contacts?.[0]?.full_name || null

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
        <Icon name={cfg.icon} customSize='18px' style={{ color: cfg.color }} />
        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: cfg.color }}>
          {cfg.label}
        </span>
      </div>

      {contactName ? (
        <>
          <div style={labelStyle}>Contact</div>
          <div style={valueStyle}>{contactName}</div>
        </>
      ) : null}

      {workflow.category ? (
        <>
          <div style={labelStyle}>Category</div>
          <div style={valueStyle}>{workflow.category}</div>
        </>
      ) : null}

      <div style={labelStyle}>Started</div>
      <div style={valueStyle}>
        {workflow.created_at ? dayjs(workflow.created_at).format('MMM D, YYYY') : 'N/A'}
      </div>

      {workflow.due_date ? (
        <>
          <div style={labelStyle}>Due</div>
          <div style={valueStyle}>{dayjs(workflow.due_date).format('MMM D, YYYY')}</div>
        </>
      ) : null}

      {workflow.description ? (
        <>
          <div style={labelStyle}>Description</div>
          <div style={valueStyle}>
            <Markup value={workflow.description} type='html' style={{ fontSize: 'inherit' }} />
          </div>
        </>
      ) : null}
    </div>
  )
}

WorkflowSummary.propTypes = {
  workflow: PropTypes.object.isRequired
}

function StepListItem ({ step, index, currentStepId, selected, onClick }) {
  const status = getStepStatus(step, currentStepId)
  const cfg = STEP_STATUS_CONFIG[status]

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 10px',
        borderRadius: '6px',
        cursor: 'pointer',
        backgroundColor: selected ? 'var(--color-primary-50, rgba(59,130,246,0.08))' : 'transparent',
        border: selected ? '1px solid var(--color-primary-200, rgba(59,130,246,0.25))' : '1px solid transparent',
        marginBottom: '4px',
        transition: 'background-color 150ms'
      }}
    >
      <Icon name={cfg.icon} customSize='16px' style={{ color: cfg.color, flexShrink: 0 }} />
      <div style={{
        flex: 1,
        minWidth: 0,
        fontSize: 'var(--font-size-sm)',
        fontWeight: selected ? 600 : 400,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
      >
        {index + 1}. {step.name || 'Untitled Step'}
      </div>
      {step.tasks_remaining > 0 ? (
        <span style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-color-subtle)',
          flexShrink: 0
        }}
        >
          {step.tasks_remaining} task{step.tasks_remaining !== 1 ? 's' : ''}
        </span>
      ) : null}
    </div>
  )
}

StepListItem.propTypes = {
  step: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  currentStepId: PropTypes.number,
  selected: PropTypes.bool,
  onClick: PropTypes.func.isRequired
}

/* ── Right panel: step detail + task checklist ─────────────────────── */

function TaskRow ({ task, stepId, readOnly, isLastOpenTask, stepHasOutcomes, onOutcomeRequired, onToggled }) {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const isComplete = !!task.completion_date

  const handleToggle = useCallback(async () => {
    if (!isComplete && isLastOpenTask && stepHasOutcomes) {
      onOutcomeRequired()
      return
    }

    setProcessing(true)
    setError(null)
    try {
      await onToggled(stepId, task.id, isComplete)
    } catch (err) {
      const responseData = err?.response?.data
      if (!isComplete && responseData?.expected_outcomes?.length) {
        onOutcomeRequired(responseData.expected_outcomes)
        return
      }
      console.error('Failed to toggle task', err)
      setError(err?.message || 'Failed to update task')
    } finally {
      setProcessing(false)
    }
  }, [stepId, task.id, isComplete, isLastOpenTask, stepHasOutcomes, onOutcomeRequired, onToggled])

  const dueStr = task.calculated_due_date || task.due_date
    ? dayjs(task.calculated_due_date || task.due_date).format('MMM D, YYYY')
    : null

  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      padding: '6px 0',
      borderBottom: '1px solid var(--color-neutral-200)',
      opacity: processing ? 0.5 : 1
    }}
    >
      <SydCheckbox
        size='small'
        value={isComplete}
        onChange={handleToggle}
        disabled={readOnly || processing}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 'var(--font-size-sm)',
          textDecoration: isComplete ? 'line-through' : 'none',
          color: isComplete ? 'var(--text-color-subtle)' : 'inherit'
        }}
        >
          {task.name || 'Untitled Task'}
        </div>
        {dueStr ? (
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-color-subtle)' }}>
            Due {dueStr}
          </div>
        ) : null}
        {error ? (
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-decline-500)' }}>
            {error}
          </div>
        ) : null}
      </div>
    </div>
  )
}

TaskRow.propTypes = {
  task: PropTypes.object.isRequired,
  stepId: PropTypes.number.isRequired,
  readOnly: PropTypes.bool,
  isLastOpenTask: PropTypes.bool,
  stepHasOutcomes: PropTypes.bool,
  onOutcomeRequired: PropTypes.func.isRequired,
  onToggled: PropTypes.func.isRequired
}

function OutcomePicker ({ outcomes, completing, stepError, onConfirm, onCancel }) {
  const [selectedOutcomeId, setSelectedOutcomeId] = useState(
    outcomes.length === 1 ? outcomes[0].id : null
  )
  const [stepNotes, setStepNotes] = useState('')

  const outcomeOptions = useMemo(() => [
    { label: 'Select an outcome...', value: '' },
    ...outcomes.map(o => ({ label: o.name, value: o.id }))
  ], [outcomes])

  return (
    <div style={{
      marginTop: '16px',
      padding: '16px',
      border: '1px solid var(--color-primary-200)',
      borderRadius: '8px',
      backgroundColor: 'var(--color-primary-50, rgba(59,130,246,0.04))'
    }}
    >
      <div style={{
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
        marginBottom: '12px'
      }}
      >
        Completing this step requires selecting an outcome.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <SydLabel label='Outcome' required>
          <SydSelect
            options={outcomeOptions}
            value={selectedOutcomeId ?? ''}
            onChange={(e) => {
              const val = e.target.value
              setSelectedOutcomeId(val ? Number(val) : null)
            }}
          />
        </SydLabel>

        <SydLabel label='Note' helperText='Optional note for this step completion'>
          <SydInput
            value={stepNotes}
            onChange={(e) => setStepNotes(e.target.value)}
            placeholder='Add a note...'
          />
        </SydLabel>

        {stepError ? (
          <div style={{ color: 'var(--color-decline-500)', fontSize: 'var(--font-size-xs)' }}>
            {stepError}
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <SydButton variant='ghost' size='sm' onClick={onCancel} disabled={completing}>
            Cancel
          </SydButton>
          <SydButton
            variant='primary'
            size='sm'
            processing={completing}
            disabled={!selectedOutcomeId}
            onClick={() => onConfirm(selectedOutcomeId, stepNotes || undefined)}
          >
            Complete Step
          </SydButton>
        </div>
      </div>
    </div>
  )
}

OutcomePicker.propTypes = {
  outcomes: PropTypes.array.isRequired,
  completing: PropTypes.bool,
  stepError: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

function StepDetail ({ step, workflow, onStepCompleted, onTaskToggled }) {
  const [completing, setCompleting] = useState(false)
  const [stepError, setStepError] = useState(null)
  const [showOutcomePicker, setShowOutcomePicker] = useState(false)
  const [fallbackOutcomes, setFallbackOutcomes] = useState(null)

  const isCurrentStep = step.id === workflow.current_step_id
  const isStepComplete = !!step.completion_date
  const isWorkflowComplete = !!workflow.completion_date

  const tasks = useMemo(() => {
    const raw = step.workflow_step_tasks ?? []
    return [...raw].sort((a, b) => (a.display_position ?? 0) - (b.display_position ?? 0))
  }, [step.workflow_step_tasks])

  const outcomes = useMemo(
    () => fallbackOutcomes ?? step.workflow_step_outcomes ?? [],
    [fallbackOutcomes, step.workflow_step_outcomes]
  )
  const openTaskCount = useMemo(() => tasks.filter(t => !t.completion_date).length, [tasks])

  const nextSequentialStepId = useMemo(() => {
    const sorted = [...(workflow.workflow_steps ?? [])]
      .sort((a, b) => (a.order_position ?? 0) - (b.order_position ?? 0))
    const idx = sorted.findIndex(s => s.id === step.id)
    return sorted[idx + 1]?.id ?? null
  }, [workflow.workflow_steps, step.id])

  useEffect(() => {
    setShowOutcomePicker(false)
    setFallbackOutcomes(null)
    setStepError(null)
  }, [step.id])

  const handleCompleteWithOutcome = useCallback(async (outcomeId, notes) => {
    setCompleting(true)
    setStepError(null)
    try {
      const selectedOutcome = outcomes.find(o => o.id === outcomeId)
      const nextStepId = selectedOutcome?.next_workflow_step_id ?? nextSequentialStepId
      await onStepCompleted(step.id, outcomeId, notes, nextStepId)
      setShowOutcomePicker(false)
    } catch (err) {
      console.error('Failed to complete step with outcome', err)
      setStepError(err?.message || 'Failed to complete step')
    } finally {
      setCompleting(false)
    }
  }, [step.id, outcomes, nextSequentialStepId, onStepCompleted])

  const handleCompleteStep = useCallback(async () => {
    if (outcomes.length > 0) {
      setShowOutcomePicker(true)
      return
    }
    setCompleting(true)
    setStepError(null)
    try {
      await onStepCompleted(step.id, null, null, nextSequentialStepId)
    } catch (err) {
      console.error('Failed to complete step', err)
      setStepError(err?.message || 'Failed to complete step')
    } finally {
      setCompleting(false)
    }
  }, [step.id, outcomes.length, nextSequentialStepId, onStepCompleted])

  const handleOutcomeRequired = useCallback((serverOutcomes) => {
    if (serverOutcomes) setFallbackOutcomes(serverOutcomes)
    setShowOutcomePicker(true)
  }, [])

  const handleCancelOutcome = useCallback(() => {
    setShowOutcomePicker(false)
    setStepError(null)
  }, [])

  return (
    <div>
      <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, marginBottom: '12px' }}>
        {step.name || 'Untitled Step'}
      </div>

      {step.display_interval ? (
        <>
          <div style={labelStyle}>Interval</div>
          <div style={valueStyle}>{step.display_interval}</div>
        </>
      ) : null}

      {step.completion_date ? (
        <>
          <div style={labelStyle}>Completed</div>
          <div style={valueStyle}>{dayjs(step.completion_date).format('MMM D, YYYY')}</div>
        </>
      ) : null}

      {step.description ? (
        <>
          <div style={labelStyle}>Description</div>
          <div style={valueStyle}>
            <Markup value={step.description} type='html' style={{ fontSize: 'inherit' }} />
          </div>
        </>
      ) : null}

      {tasks.length > 0 ? (
        <div style={{ marginTop: '8px' }}>
          <div style={{
            fontWeight: 700,
            fontSize: 'var(--font-size-xs)',
            textTransform: 'uppercase',
            marginBottom: '6px'
          }}
          >
            Tasks ({tasks.filter(t => !!t.completion_date).length}/{tasks.length})
          </div>
          {tasks.map(task => {
            const isLastOpen = !task.completion_date && openTaskCount === 1
            const taskReadOnly = !isCurrentStep || isStepComplete || isWorkflowComplete
            return (
              <TaskRow
                key={task.id}
                task={task}
                stepId={step.id}
                readOnly={taskReadOnly}
                isLastOpenTask={isLastOpen}
                stepHasOutcomes={outcomes.length > 0}
                onOutcomeRequired={handleOutcomeRequired}
                onToggled={onTaskToggled}
              />
            )
          })}
        </div>
      ) : (
        <div style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-color-subtle)',
          marginTop: '8px'
        }}
        >
          No tasks for this step
        </div>
      )}

      {showOutcomePicker && outcomes.length > 0 ? (
        <OutcomePicker
          outcomes={outcomes}
          completing={completing}
          stepError={stepError}
          onConfirm={handleCompleteWithOutcome}
          onCancel={handleCancelOutcome}
        />
      ) : null}

      {isCurrentStep && !isStepComplete && !isWorkflowComplete && !showOutcomePicker ? (
        <div style={{ marginTop: '16px' }}>
          <SydButton
            variant='primary'
            size='sm'
            processing={completing}
            onClick={handleCompleteStep}
          >
            Complete Step
          </SydButton>
          {stepError ? (
            <div style={{ color: 'var(--color-decline-500)', fontSize: 'var(--font-size-xs)', marginTop: '8px' }}>
              {stepError}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

StepDetail.propTypes = {
  step: PropTypes.object.isRequired,
  workflow: PropTypes.object.isRequired,
  onStepCompleted: PropTypes.func.isRequired,
  onTaskToggled: PropTypes.func.isRequired
}

/* ── Modal container ───────────────────────────────────────────────── */

function WorkflowDetailModal ({ open, onClose, workflowId }) {
  const { workflow, toggleTask, completeStep } = useWorkflowDetail(workflowId)

  const steps = useMemo(() => {
    if (!workflow?.workflow_steps?.length) return []
    return [...workflow.workflow_steps].sort((a, b) => (a.order_position ?? 0) - (b.order_position ?? 0))
  }, [workflow?.workflow_steps])

  const currentStepIndex = useMemo(() => {
    if (!workflow?.current_step_id || !steps.length) return 0
    const idx = steps.findIndex(s => s.id === workflow.current_step_id)
    return idx >= 0 ? idx : 0
  }, [workflow?.current_step_id, steps])

  const [selectedStepIndex, setSelectedStepIndex] = useState(currentStepIndex)

  useEffect(() => {
    setSelectedStepIndex(currentStepIndex)
  }, [currentStepIndex])

  const selectedStep = steps[selectedStepIndex] ?? steps[0] ?? null

  if (!workflow) return null

  return (
    <SydModal
      open={open}
      onClose={onClose}
      title={workflow.name || 'Workflow Details'}
      size='large'
      disablePortal={false}
    >
      <div style={{
        display: 'flex',
        gap: '24px',
        minHeight: '400px'
      }}
      >
        {/* Left panel */}
        <div style={{
          flex: '0 0 45%',
          borderRight: '1px solid var(--color-neutral-200)',
          paddingRight: '24px',
          overflowY: 'auto'
        }}
        >
          <WorkflowSummary workflow={workflow} />

          {steps.length > 0 ? (
            <div style={{ marginTop: '16px' }}>
              <div style={{
                fontWeight: 700,
                fontSize: 'var(--font-size-xs)',
                textTransform: 'uppercase',
                marginBottom: '8px'
              }}
              >
                Steps
              </div>
              {steps.map((step, idx) => (
                <StepListItem
                  key={step.id}
                  step={step}
                  index={idx}
                  currentStepId={workflow.current_step_id}
                  selected={idx === selectedStepIndex}
                  onClick={() => setSelectedStepIndex(idx)}
                />
              ))}
            </div>
          ) : null}
        </div>

        {/* Right panel */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          minWidth: 0
        }}
        >
          {selectedStep ? (
            <StepDetail
              step={selectedStep}
              workflow={workflow}
              onStepCompleted={completeStep}
              onTaskToggled={toggleTask}
            />
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'var(--text-color-subtle)',
              fontSize: 'var(--font-size-sm)'
            }}
            >
              No steps available
            </div>
          )}
        </div>
      </div>

      <SydModalActions style={{ paddingTop: '16px' }}>
        <SydButton variant='outline' size='sm' onClick={onClose}>
          Close
        </SydButton>
      </SydModalActions>
    </SydModal>
  )
}

WorkflowDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  workflowId: PropTypes.number
}

export default WorkflowDetailModal
